import {
  countMaps,
  createMap,
  getAppFlag,
  setAppFlag,
  setLastOpenedMapId,
} from "@/src/core/persistence/repository";
import type { PersistedMap } from "@/src/core/persistence/schema";
import type { MapDocument } from "@/src/types/map";

/** The key zustand's persist middleware wrote to before Phase 2.5. */
export const LEGACY_STORAGE_KEY = "dungeon-builder-map-v1";
const MIGRATED_FLAG = "legacyLocalStorageMigrated";

/**
 * One-time rescue of the pre-Phase-2.5 localStorage map into IndexedDB.
 *
 * Deliberately non-destructive: the original localStorage key is left in place.
 * If anything about this migration turns out to be wrong, the user's only copy
 * of their map is still sitting where it always was.
 */
export const migrateLegacyLocalStorage = async (): Promise<PersistedMap | null> => {
  if (typeof localStorage === "undefined") return null;
  if (await getAppFlag(MIGRATED_FLAG)) return null;

  let raw: string | null = null;
  try {
    raw = localStorage.getItem(LEGACY_STORAGE_KEY);
  } catch {
    // Private-mode or blocked storage. Nothing to rescue.
    return null;
  }
  if (!raw) {
    await setAppFlag(MIGRATED_FLAG, true);
    return null;
  }

  // Never import legacy data on top of an existing library.
  if (await countMaps() > 0) {
    await setAppFlag(MIGRATED_FLAG, true);
    return null;
  }

  try {
    const envelope = JSON.parse(raw) as { state?: Record<string, unknown> };
    const state = envelope?.state;
    if (!state || typeof state !== "object") {
      await setAppFlag(MIGRATED_FLAG, true);
      return null;
    }

    const meta = state.meta as MapDocument["meta"] | undefined;
    const floorPolygons = state.floorPolygons as MapDocument["geometry"]["floorPolygons"] | undefined;
    if (!meta || typeof meta.title !== "string" || !Array.isArray(floorPolygons)) {
      await setAppFlag(MIGRATED_FLAG, true);
      return null;
    }

    const document: MapDocument = {
      version: "1.0.0",
      meta,
      geometry: {
        floorPolygons,
        hexCells: Array.isArray(state.hexCells) ? state.hexCells as MapDocument["geometry"]["hexCells"] : [],
      },
      objects: Array.isArray(state.objects) ? state.objects as MapDocument["objects"] : [],
    };

    const viewport = (state.viewport && typeof state.viewport === "object")
      ? state.viewport as PersistedMap["viewport"]
      : { x: 0, y: 0, zoom: 1 };
    const nextRoomNumber = typeof state.nextRoomNumber === "number" ? state.nextRoomNumber : 1;

    const record = await createMap(document, viewport, nextRoomNumber);
    await setLastOpenedMapId(record.id);
    await setAppFlag(MIGRATED_FLAG, true);
    return record;
  } catch (error) {
    console.warn("Could not migrate the legacy localStorage map; leaving it untouched.", error);
    await setAppFlag(MIGRATED_FLAG, true);
    return null;
  }
};
